import { useState, useEffect } from 'react';
import { Activity, Search, Loader2, ChevronLeft, ChevronRight } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { adminAPI } from '@/lib/api';
import { cn } from '@/lib/utils';

const ACTION_TYPES = ['login', 'register', 'verify_email', 'role_change', 'user_created', 'user_deactivated', 'user_reactivated', 'admin_password_reset', 'exemption_change', 'settings_change'];

const actionColors = {
  login: 'border-vrs-green/30 text-vrs-green',
  register: 'border-vrs-cyan/30 text-vrs-cyan',
  role_change: 'border-vrs-amber/30 text-vrs-amber',
  user_deactivated: 'border-vrs-red/30 text-vrs-red',
  admin_password_reset: 'border-vrs-amber/30 text-vrs-amber',
};

export function ActivityLogTab() {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [actionFilter, setActionFilter] = useState('');
  const [userSearch, setUserSearch] = useState('');
  const [page, setPage] = useState(0);
  const PAGE_SIZE = 50;

  useEffect(() => {
    const fetch = async () => {
      setLoading(true);
      try {
        const res = await adminAPI.getActivity({ action_type: actionFilter, limit: PAGE_SIZE, skip: page * PAGE_SIZE });
        setLogs(res.data.activity || []);
        setTotal(res.data.total || 0);
      } catch { setLogs([]); }
      setLoading(false);
    };
    fetch();
  }, [actionFilter, page]);

  const filtered = userSearch ? logs.filter(l => l.actor_email?.toLowerCase().includes(userSearch.toLowerCase())) : logs;

  return (
    <div className="space-y-4" data-testid="admin-activity-log">
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input placeholder="Filter by email..." value={userSearch} onChange={e => setUserSearch(e.target.value)} className="pl-9 h-10 bg-muted/30 border-border" />
        </div>
        <Select value={actionFilter} onValueChange={v => { setActionFilter(v === 'all' ? '' : v); setPage(0); }}>
          <SelectTrigger className="w-[200px] h-10 bg-muted/30 border-border" data-testid="activity-action-filter">
            <SelectValue placeholder="All actions" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All actions</SelectItem>
            {ACTION_TYPES.map(a => <SelectItem key={a} value={a}>{a.replace(/_/g, ' ')}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <Card className="border-border/60">
        <CardContent className="p-4">
          {loading ? (
            <div className="flex justify-center py-12"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
          ) : filtered.length === 0 ? (
            <p className="text-center text-sm text-muted-foreground py-12">No activity recorded yet</p>
          ) : (
            <div className="space-y-2">
              {filtered.map(log => (
                <div key={log.id} className="flex items-start gap-3 rounded-lg border border-border/40 px-4 py-3 hover:bg-muted/10 transition-[background-color] duration-200">
                  <Activity className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge variant="outline" className={cn('text-[10px] capitalize', actionColors[log.action_type] || 'text-muted-foreground')}>
                        {log.action_type?.replace(/_/g, ' ')}
                      </Badge>
                      <span className="text-sm font-medium text-foreground">{log.actor_email || 'System'}</span>
                      {log.target_user_id && log.target_user_id !== log.actor_user_id && (
                        <span className="text-xs text-muted-foreground">\u2192 {log.target_user_id.slice(0, 8)}...</span>
                      )}
                    </div>
                    {log.metadata && Object.keys(log.metadata).length > 0 && (
                      <p className="text-xs text-muted-foreground mt-1 font-mono">{JSON.stringify(log.metadata)}</p>
                    )}
                  </div>
                  <span className="text-[10px] text-muted-foreground font-mono shrink-0 mt-0.5">
                    {log.timestamp ? new Date(log.timestamp).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : ''}
                  </span>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {total > PAGE_SIZE && (
        <div className="flex items-center justify-between">
          <p className="text-xs text-muted-foreground">Showing {page * PAGE_SIZE + 1}-{Math.min((page + 1) * PAGE_SIZE, total)} of {total}</p>
          <div className="flex gap-1">
            <Button variant="ghost" size="sm" disabled={page === 0} onClick={() => setPage(p => p - 1)}><ChevronLeft className="h-4 w-4" /></Button>
            <Button variant="ghost" size="sm" disabled={(page + 1) * PAGE_SIZE >= total} onClick={() => setPage(p => p + 1)}><ChevronRight className="h-4 w-4" /></Button>
          </div>
        </div>
      )}
    </div>
  );
}
